import { useState } from 'react';
import { Building2, PackageOpen, UserPlus, UserRound } from 'lucide-react';
import type { HolderInput } from '@khadi/shared';
import { useAuth } from '../lib/auth';
import { useEmployees, useLocations } from '../lib/queries';
import { EmployeeCreateDialog } from './EmployeeCreateDialog';
import { cn } from './ui';

/**
 * Auswahl des Zuweisungsziels: Person, Standort oder Lager.
 *
 * Beide Ziele sind gleichwertig — 17 der übernommenen Geräte hängen an
 * einem Standort (Pickwagen-Tablets, Scanner) und nicht an einer Person.
 *
 * Eine noch unbekannte Person lässt sich hier direkt anlegen: Bei einer
 * Neuanstellung liegt das Gerät schon auf dem Tisch, und der Umweg über
 * die Mitarbeiterseite würde die Erfassung abbrechen.
 */
export function HolderPicker({
  value,
  onChange,
  allowStock = true,
  allowCreateEmployee = true,
  autoFocus,
}: {
  value: HolderInput;
  onChange: (value: HolderInput) => void;
  allowStock?: boolean;
  allowCreateEmployee?: boolean;
  autoFocus?: boolean;
}) {
  const auth = useAuth();
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const employees = useEmployees({ is_active: true });
  const locations = useLocations();

  const canCreate = allowCreateEmployee && auth.can('employee.write');

  const filtered = employees.data?.rows.filter((employee) =>
    employeeSearch.trim()
      ? employee.name.toLowerCase().includes(employeeSearch.trim().toLowerCase())
      : true,
  );

  return (
    <div>
      <div role="tablist" className="mb-3 grid grid-cols-3 gap-1.5">
        <TargetTab
          active={value.type === 'employee'}
          icon={UserRound}
          label="Person"
          onClick={() => onChange({ type: 'employee', id: 0 } as HolderInput)}
        />
        <TargetTab
          active={value.type === 'location'}
          icon={Building2}
          label="Standort"
          onClick={() => onChange({ type: 'location', id: 0 } as HolderInput)}
        />
        {allowStock && (
          <TargetTab
            active={value.type === 'none'}
            icon={PackageOpen}
            label="Lager"
            onClick={() => onChange({ type: 'none' })}
          />
        )}
      </div>

      {value.type === 'employee' && (
        <div className="animate-fade-in space-y-3">
          <div className="flex gap-2">
            <input
              autoFocus={autoFocus}
              value={employeeSearch}
              onChange={(event) => setEmployeeSearch(event.target.value)}
              placeholder="Name eingeben"
              className="field"
              aria-label="Mitarbeiter suchen"
            />
            {canCreate && (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="btn btn-ghost btn-md shrink-0"
                title="Neue Person anlegen"
              >
                <UserPlus className="h-4 w-4" aria-hidden />
                Neu
              </button>
            )}
          </div>
          <div className="max-h-56 overflow-y-auto rounded-[var(--radius-md)] border border-[var(--color-line)]">
            {filtered?.length === 0 && (
              <div className="px-3 py-4 text-center">
                <p className="text-sm text-[var(--color-fg3)]">Niemand gefunden.</p>
                {canCreate && (
                  <button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    className="btn btn-quiet btn-sm mt-2"
                  >
                    <UserPlus className="h-3.5 w-3.5" aria-hidden />
                    {employeeSearch.trim() ? `„${employeeSearch.trim()}" anlegen` : 'Person anlegen'}
                  </button>
                )}
              </div>
            )}
            {filtered?.map((employee) => (
              <button
                key={employee.id}
                type="button"
                onClick={() => onChange({ type: 'employee', id: employee.id })}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-5 py-4 text-left text-sm transition-colors',
                  value.type === 'employee' && value.id === employee.id
                    ? 'bg-[var(--color-khadi-forest-800)] text-[var(--color-khadi-cream-50)]'
                    : 'hover:bg-[var(--color-bg-tinted)]',
                )}
              >
                <span className="truncate">{employee.name}</span>
                <span className="tabular shrink-0 text-sm opacity-70">
                  {employee.assets_count > 0 ? `${employee.assets_count} Geräte` : ''}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {value.type === 'location' && (
        <div className="animate-fade-in grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {locations.data?.rows.map((location) => (
            <button
              key={location.id}
              type="button"
              onClick={() => onChange({ type: 'location', id: location.id })}
              className={cn(
                'rounded-[var(--radius-md)] border px-5 py-4 text-sm transition-colors',
                value.type === 'location' && value.id === location.id
                  ? 'border-[var(--color-khadi-forest-800)] bg-[var(--color-khadi-forest-800)] text-[var(--color-khadi-cream-50)]'
                  : 'border-[var(--color-line)] hover:bg-[var(--color-bg-tinted)]',
              )}
            >
              {location.name}
            </button>
          ))}
        </div>
      )}

      {value.type === 'none' && (
        <p className="animate-fade-in rounded-[var(--radius-md)] bg-[var(--color-bg-tinted)] px-4 py-3.5 text-sm text-[var(--color-fg2)]">
          Das Gerät liegt danach im Lager und gilt als verfügbar.
        </p>
      )}

      {createOpen && (
        <EmployeeCreateDialog
          defaultName={employeeSearch}
          onClose={() => setCreateOpen(false)}
          onCreated={(employee) => {
            // Direkt als Ziel setzen und die Suche auf den Namen stellen,
            // damit die Auswahl sichtbar bleibt, sobald die Liste nachlädt.
            onChange({ type: 'employee', id: employee.id });
            setEmployeeSearch(employee.name);
          }}
        />
      )}
    </div>
  );
}

function TargetTab({
  active,
  icon: Icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: typeof UserRound;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex items-center justify-center gap-1.5 rounded-[var(--radius-md)] border px-2 py-2 text-sm transition-colors duration-150',
        active
          ? 'border-[var(--color-accent)] bg-[var(--color-khadi-clay-100)] font-semibold text-[var(--color-khadi-clay-700)]'
          : 'border-[var(--color-line)] text-[var(--color-fg2)] hover:bg-[var(--color-bg-tinted)]',
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
      {label}
    </button>
  );
}

/** Ist die Auswahl vollständig? (id 0 heißt: Art gewählt, Ziel noch offen) */
export function isHolderComplete(value: HolderInput): boolean {
  return value.type === 'none' || value.id > 0;
}
